/**
 * rastreador.tsx — painel flutuante "Tarefas em Andamento".
 *
 * Porte do static/js/global_task_tracker.js (667 linhas de DOM imperativo)
 * para React. O que ele fazia e continua fazendo: registrar a geração em
 * curso, sobreviver à navegação (localStorage), acompanhar o progresso e
 * levar ao documento quando conclui.
 *
 * O que MUDA: o polling e a normalização não são mais dele. Cada linha usa
 * o mesmo useTaskStatus/`GET /api/v1/tasks/...` da página de status — some
 * a divergência entre o número do painel e o da tela.
 */

import {
  faCheckCircle,
  faClock,
  faExclamationTriangle,
  faExpand,
  faHandPointer,
  faMinus,
  faSyncAlt,
  faTasks,
  faTimes,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { EstadoTask, TipoTask } from '../../api/tasks'
import { useTaskStatus } from '../../hooks/useTaskStatus'
import { CONFIGURACAO } from './configuracao'

const VALIDADE_MS = 24 * 60 * 60 * 1000 // 24h, como no legado
const SUMIR_APOS_CONCLUIR_MS = 15_000

export interface TarefaRastreada {
  tipo: TipoTask
  id: number
  criadaEm: number
}

interface EstadoPersistido {
  tarefas: TarefaRastreada[]
  minimizado: boolean
  fechado: boolean
}

interface ValorContexto {
  tarefas: TarefaRastreada[]
  registrar: (tipo: TipoTask, id: number) => void
  remover: (tipo: TipoTask, id: number) => void
}

const Contexto = createContext<ValorContexto>({
  tarefas: [],
  registrar: () => {},
  remover: () => {},
})

export const useRastreador = () => useContext(Contexto)

const chave = (usuario: string) => `licitia_tarefas_spa_v1_${usuario}`
const idDe = (t: { tipo: TipoTask; id: number }) => `${t.tipo}:${t.id}`

function ler(usuario: string): EstadoPersistido {
  const vazio: EstadoPersistido = { tarefas: [], minimizado: false, fechado: false }
  try {
    const bruto = localStorage.getItem(chave(usuario))
    if (!bruto) return vazio
    const dados = JSON.parse(bruto) as EstadoPersistido
    const agora = Date.now()
    return {
      tarefas: (dados.tarefas ?? []).filter((t) => agora - t.criadaEm < VALIDADE_MS),
      minimizado: !!dados.minimizado,
      fechado: !!dados.fechado,
    }
  } catch {
    return vazio // storage corrompido não pode derrubar o app
  }
}

export function RastreadorProvider({
  usuario,
  children,
}: {
  usuario: string
  children: React.ReactNode
}) {
  const [estado, setEstado] = useState<EstadoPersistido>(() => ler(usuario))

  // Troca de usuário na mesma aba: recarrega o estado da chave certa
  useEffect(() => setEstado(ler(usuario)), [usuario])

  useEffect(() => {
    try {
      localStorage.setItem(chave(usuario), JSON.stringify(estado))
    } catch {
      /* quota cheia: o painel funciona só nesta navegação */
    }
  }, [usuario, estado])

  const registrar = useCallback((tipo: TipoTask, id: number) => {
    setEstado((atual) => {
      if (atual.tarefas.some((t) => idDe(t) === idDe({ tipo, id }))) return atual
      return {
        ...atual,
        fechado: false, // tarefa nova reabre o painel, como no original
        tarefas: [...atual.tarefas, { tipo, id, criadaEm: Date.now() }],
      }
    })
  }, [])

  const remover = useCallback((tipo: TipoTask, id: number) => {
    setEstado((atual) => ({
      ...atual,
      tarefas: atual.tarefas.filter((t) => idDe(t) !== idDe({ tipo, id })),
    }))
  }, [])

  const valor = useMemo(
    () => ({ tarefas: estado.tarefas, registrar, remover }),
    [estado.tarefas, registrar, remover],
  )

  return (
    <Contexto.Provider value={valor}>
      {children}
      <Painel
        estado={estado}
        aoAlternarMinimizado={() =>
          setEstado((a) => ({ ...a, minimizado: !a.minimizado }))
        }
        aoFechar={() => setEstado((a) => ({ ...a, fechado: true }))}
        aoAbrir={() => setEstado((a) => ({ ...a, fechado: false }))}
        aoRemover={remover}
      />
    </Contexto.Provider>
  )
}

/**
 * Acompanha uma tarefa mesmo com o painel minimizado ou fechado.
 *
 * A `LinhaTarefa` só existe quando o painel está aberto — enquanto
 * minimizado, ninguém consultava o status, ninguém sabia que a geração
 * terminou e o descarte automático nunca disparava. Este componente não
 * desenha nada; só observa. O TanStack Query compartilha a consulta pela
 * queryKey, então observar aqui e desenhar na linha não dobra requisição.
 */
function ObservadorTarefa({
  tarefa,
  minimizado,
  aoMudarEstado,
  aoRemover,
}: {
  tarefa: TarefaRastreada
  minimizado: boolean
  aoMudarEstado: (id: string, estado?: EstadoTask) => void
  aoRemover: () => void
}) {
  const { data } = useTaskStatus(tarefa.tipo, tarefa.id)
  const estado = data?.estado
  const id = idDe(tarefa)

  useEffect(() => {
    aoMudarEstado(id, estado)
  }, [id, estado, aoMudarEstado])

  // Some sozinha só com o painel ABERTO: minimizado, sumir seria apagar o
  // único aviso de que a geração terminou — que é justamente o que o
  // usuário precisa ver ao voltar.
  useEffect(() => {
    if (estado !== 'CONCLUIDO' || minimizado) return undefined
    const t = setTimeout(aoRemover, SUMIR_APOS_CONCLUIR_MS)
    return () => clearTimeout(t)
  }, [estado, minimizado, aoRemover])

  return null
}


function LinhaTarefa({
  tarefa,
  aoRemover,
}: {
  tarefa: TarefaRastreada
  aoRemover: () => void
}) {
  const { data } = useTaskStatus(tarefa.tipo, tarefa.id)
  const config = CONFIGURACAO[tarefa.tipo]
  const estado = data?.estado
  const percentual = data?.progresso.percentual ?? 0

  const falhou = estado === 'FALHOU'
  const concluiu = estado === 'CONCLUIDO'
  const aguardando = estado === 'AGUARDANDO_SELECAO'
  const icone = falhou
    ? faExclamationTriangle
    : concluiu
      ? faCheckCircle
      : aguardando
        ? faHandPointer
        : config.icone
  const corBarra = falhou
    ? 'bg-red-500'
    : concluiu
      ? 'bg-gradient-to-r from-emerald-500 to-green-500'
      : aguardando
        ? 'bg-gradient-to-r from-amber-500 to-amber-600'
        : 'bg-gradient-to-r from-indigo-500 to-purple-600'

  return (
    <li className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-dark-bg/60">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
          <FontAwesomeIcon
            icon={icone}
            spin={!falhou && !concluiu && !aguardando}
            className="text-indigo-500"
          />
          <span className="truncate">{config.rotulo}</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-500 dark:text-slate-400">
            {percentual}%
          </span>
          <button
            type="button"
            onClick={aoRemover}
            aria-label={`Remover ${config.rotulo} do painel`}
            className="text-slate-400 transition-colors hover:text-red-500"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </span>
      </div>

      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
        <div
          className={`h-full rounded-full transition-all duration-500 ${corBarra}`}
          style={{ width: `${concluiu ? 100 : percentual}%` }}
        />
      </div>

      <p className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-500 dark:text-slate-400">
        <FontAwesomeIcon
          icon={falhou ? faExclamationTriangle : concluiu ? faCheckCircle : faClock}
          className="mt-0.5"
        />
        <span className="min-w-0 break-words">
          {data?.erro?.mensagem ?? data?.progresso.mensagem ?? 'Processando…'}
        </span>
      </p>

      {aguardando && data?.acao_pendente && (
        <a
          href={data.acao_pendente.url}
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-2 text-xs font-semibold text-white shadow"
        >
          <FontAwesomeIcon icon={faHandPointer} />
          {data.acao_pendente.tipo === 'selecao_precos'
            ? 'Selecionar Preços'
            : 'Selecionar Documentos'}
        </a>
      )}

      {concluiu && data?.resultado && (
        <a
          href={data.resultado.view_url}
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 px-4 py-2 text-xs font-semibold text-white shadow"
        >
          <FontAwesomeIcon icon={faCheckCircle} />
          {config.rotuloVisualizar}
        </a>
      )}
    </li>
  )
}

function Painel({
  estado,
  aoAlternarMinimizado,
  aoFechar,
  aoAbrir,
  aoRemover,
}: {
  estado: EstadoPersistido
  aoAlternarMinimizado: () => void
  aoFechar: () => void
  aoAbrir: () => void
  aoRemover: (tipo: TipoTask, id: number) => void
}) {
  // Estado de cada tarefa, alimentado pelos observadores — que continuam
  // montados com o painel minimizado ou fechado.
  const [estados, setEstados] = useState<Record<string, EstadoTask | undefined>>({})
  const aoMudarEstado = useCallback((id: string, valor?: EstadoTask) => {
    setEstados((anterior) =>
      anterior[id] === valor ? anterior : { ...anterior, [id]: valor },
    )
  }, [])

  const vivos = estado.tarefas.map((t) => estados[idDe(t)])
  const resumo = vivos.some((e) => e === 'FALHOU')
    ? 'falhou'
    : vivos.some((e) => e === 'AGUARDANDO_SELECAO')
      ? 'aguardando'
      : vivos.length > 0 && vivos.every((e) => e === 'CONCLUIDO')
        ? 'concluido'
        : 'processando'

  const observadores = estado.tarefas.map((tarefa) => (
    <ObservadorTarefa
      key={`obs:${idDe(tarefa)}`}
      tarefa={tarefa}
      minimizado={estado.minimizado || estado.fechado}
      aoMudarEstado={aoMudarEstado}
      aoRemover={() => aoRemover(tarefa.tipo, tarefa.id)}
    />
  ))

  if (estado.tarefas.length === 0) return null

  const APARENCIA = {
    processando: {
      faixa: 'from-indigo-600 to-purple-700',
      bolha: 'from-indigo-600 to-purple-700',
      contador: 'bg-red-500',
      icone: faSyncAlt,
      girando: true,
      titulo: 'Tarefas em Andamento',
    },
    concluido: {
      faixa: 'from-emerald-600 to-green-600',
      bolha: 'from-emerald-600 to-green-600',
      contador: 'bg-emerald-500',
      icone: faCheckCircle,
      girando: false,
      titulo: 'Geração concluída',
    },
    falhou: {
      faixa: 'from-red-600 to-rose-700',
      bolha: 'from-red-600 to-rose-700',
      contador: 'bg-red-500',
      icone: faExclamationTriangle,
      girando: false,
      titulo: 'Falha na geração',
    },
    aguardando: {
      faixa: 'from-amber-500 to-amber-600',
      bolha: 'from-amber-500 to-amber-600',
      contador: 'bg-amber-500',
      icone: faHandPointer,
      girando: false,
      titulo: 'Ação necessária',
    },
  }[resumo]

  // Terminou e o usuário não está vendo a lista: o cabeçalho é o único aviso.
  const avisar = estado.minimizado && resumo !== 'processando'

  if (estado.fechado) {
    return (
      <>
        {observadores}
        <button
          type="button"
          onClick={aoAbrir}
          aria-label={`Abrir tarefas em andamento — ${APARENCIA.titulo}`}
          className={`fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br ${APARENCIA.bolha} text-white shadow-xl transition-transform hover:scale-105`}
        >
          <FontAwesomeIcon
            icon={resumo === 'processando' ? faTasks : APARENCIA.icone}
            className="text-lg"
          />
          <span
            className={`absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full ${APARENCIA.contador} px-1 text-xs font-bold`}
          >
            {estado.tarefas.length}
          </span>
        </button>
      </>
    )
  }

  return (
    <section
      aria-label="Tarefas em andamento"
      aria-live="polite"
      className="fixed bottom-6 right-6 z-40 w-[22rem] max-w-[calc(100vw-3rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-dark-card"
    >
      {observadores}
      <header
        className={`flex items-center justify-between gap-2 bg-gradient-to-r ${APARENCIA.faixa} px-4 py-3 text-white transition-colors duration-500`}
      >
        {/* Minimizado, o cabeçalho é a única coisa visível: clicar nele abre. */}
        <h2
          onClick={estado.minimizado ? aoAlternarMinimizado : undefined}
          className={`flex items-center gap-2 text-sm font-semibold ${
            estado.minimizado ? 'cursor-pointer' : ''
          }`}
        >
          <FontAwesomeIcon
            icon={APARENCIA.icone}
            spin={APARENCIA.girando}
            className={avisar ? 'animate-pulse' : undefined}
          />
          {APARENCIA.titulo}
          <span className="rounded-full bg-white/25 px-2 py-0.5 text-xs font-bold">
            {estado.tarefas.length}
          </span>
          {avisar && (
            <span className="text-xs font-normal opacity-90">— toque para ver</span>
          )}
        </h2>
        <span className="flex items-center gap-1">
          <button
            type="button"
            onClick={aoAlternarMinimizado}
            aria-label={estado.minimizado ? 'Expandir painel' : 'Minimizar painel'}
            className="rounded p-1 transition-colors hover:bg-white/20"
          >
            <FontAwesomeIcon icon={estado.minimizado ? faExpand : faMinus} />
          </button>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar painel de tarefas"
            className="rounded p-1 transition-colors hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </span>
      </header>

      {!estado.minimizado && (
        <>
          <ul className="max-h-80 space-y-2 overflow-y-auto p-3">
            {estado.tarefas.map((tarefa) => (
              <LinhaTarefa
                key={idDe(tarefa)}
                tarefa={tarefa}
                aoRemover={() => aoRemover(tarefa.tipo, tarefa.id)}
              />
            ))}
          </ul>
          <footer className="flex items-center gap-2 border-t border-slate-200 bg-slate-50 px-4 py-2 text-xs text-slate-500 dark:border-slate-700 dark:bg-dark-bg/40 dark:text-slate-400">
            <FontAwesomeIcon icon={faInfoCircle} />
            Você pode navegar normalmente
          </footer>
        </>
      )}
    </section>
  )
}
